/**
 * MediaPipe results → the mapping layer's Hand values, and the expression /
 * pitch role assignment.
 *
 * PURE and MediaPipe-free by design: this module takes plain arrays, not a
 * HandLandmarkerResult, so it can be tested from a recorded fixture with no
 * camera, no WASM and no DOM. src/vision/camera.ts does the narrow job of
 * unwrapping the real result into these shapes.
 */

import type { Features, FeatureFrame, Hand, Vec3 } from '../mapping/types';
import { featuresFromHand, type Ranges } from '../mapping/features';
import { NEUTRAL_FEATURES } from '../mapping/types';

/** One hand as it arrives from the detector, before role assignment. */
export interface RawHand {
  points: Vec3[];
  /** RAW MediaPipe handedness label — NOT corrected for mirroring. */
  label: 'Left' | 'Right';
  /** Handedness classification score. See fixtures/README.md on why this is secondary. */
  score: number;
}

/**
 * Correct MediaPipe's handedness for mirroring.
 *
 * MediaPipe labels handedness as if the frame were mirrored. The spike feeds it
 * raw (spike/index.html:345), so the label is inverted from the user's point of
 * view and a raw 'Left' is the user's RIGHT hand. `swapRoles` flips that again
 * for a rear camera or an already-mirrored source, and is recorded in every
 * fixture header precisely because role assignment cannot be reproduced
 * without it.
 */
export const isUserRight = (label: 'Left' | 'Right', swapRoles = false): boolean =>
  (label === 'Left') !== swapRoles;

/**
 * Assign the expression and pitch roles.
 *
 * The user's right hand is the expression hand. With one hand visible it takes
 * the expression role whichever it is — a one-handed player must still be able
 * to play. With two, the remaining hand becomes the pitch hand.
 */
export function assignRoles(
  hands: readonly RawHand[],
  swapRoles = false,
  maxHands = 2,
): { expr: RawHand | null; pitch: RawHand | null } {
  if (hands.length === 0) return { expr: null, pitch: null };
  if (maxHands === 1) return { expr: hands[0], pitch: null };

  let expr: RawHand | null = null;
  let pitch: RawHand | null = null;
  for (const h of hands) {
    if (isUserRight(h.label, swapRoles) && !expr) expr = h;
    else if (!pitch) pitch = h;
  }
  // Two left hands, or one left hand alone: whoever is here plays expression.
  if (!expr && pitch) { expr = pitch; pitch = null; }
  return { expr, pitch };
}

const toHand = (raw: RawHand, swapRoles: boolean): Hand => ({
  points: raw.points,
  handedness: isUserRight(raw.label, swapRoles) ? 'right' : 'left',
  score: raw.score,
});

/**
 * Build the FeatureFrame the whole mapping layer consumes. `dt` is seconds
 * since the previous frame — the caller owns the clock, so a fixture replay and
 * a live camera produce identical frames.
 */
export function frameFromHands(
  hands: readonly RawHand[],
  dt: number,
  swapRoles = false,
  maxHands = 2,
  ranges?: Ranges,
): FeatureFrame {
  const { expr, pitch } = assignRoles(hands, swapRoles, maxHands);
  const exprF: Features = expr
    ? featuresFromHand(toHand(expr, swapRoles), ranges)
    : { ...NEUTRAL_FEATURES };
  // sourceHasPitch is a property of the SOURCE's configuration, not of this
  // frame: a two-hand camera with one hand momentarily missing is a dropout
  // (the degree latches); a one-hand source simply has no pitch channel.
  const frame: FeatureFrame = { expr: exprF, dt, sourceHasPitch: maxHands >= 2 };
  if (pitch) frame.pitch = featuresFromHand(toHand(pitch, swapRoles), ranges);
  return frame;
}
